import React, { useState, useEffect, useRef } from "react";
import {
  fetchBotSteps,
  updateBotStepPrompt,
  getAuthenticatedApps,
} from "../../services/api"; // Assuming you have these API functions
import { DataState } from "./../../context/DataProvider";
import { WORKFLOW_API_URL } from "./../../services/workflowApi"; // For fetching app event data
import { integrationData, dataSources, appImages } from "../../utils/constants";
import { authenticateApp } from "../../utils/auth";
import { FaPen } from "react-icons/fa6";
import TriggerNodeModal from "../workflow3/TriggerNodeModal";

const apps_data = [...integrationData, ...dataSources];

const BotStepsEditModal = ({ botId, onClose }) => {
  const [botSteps, setBotSteps] = useState([]);
  const [editingStepId, setEditingStepId] = useState(null);
  const [editedPrompt, setEditedPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const { userGoogleId, bots } = DataState();
  const textareaRef = useRef(null);

  const [error, setError] = useState(""); // State to handle error messages
  const [integratedApps, setIntegratedApps] = useState([]);
  const [notIntegratedApps, setNotIntegratedApps] = useState([]);
  // const [selectedApp, setSelectedApp] = useState(null);
  const [selectedAppForIntegration, setSelectedAppForIntegration] =
    useState(null);

  // State for the Trigger View Modal
  const [triggers, setTriggers] = useState([]); // State for triggers
  const [isTriggerModalOpen, setIsTriggerModalOpen] = useState(false);
  const [currentTriggerData, setCurrentTriggerData] = useState(null);
  const [appEventsData, setAppEventsData] = useState([]);

  // Function to fetch data needed by TriggerNodeModal
  const fetchAppsandEventsData = async (appIntegrations) => {
    console.log("Apps integrations for event fetch:", appIntegrations)
    try {
      const response = await fetch(
        `${WORKFLOW_API_URL}/canvas/appsAndTriggers`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userGoogleId}`,
          },
          credentials: "include",
          body: JSON.stringify({ apps: appIntegrations }),
        }
      );
      if (!response.ok) throw new Error("Failed to fetch app event data");
      const data = await response.json();
      console.log("Apps events:", data.appsEventsData)
      setAppEventsData(data.appsEventsData || []);
    } catch (error) {
      console.error("Error fetching apps and events data:", error);
    }
  };

  // Handler to open the trigger modal in view-only mode
  const handleViewTrigger = (triggerNode) => {
    setCurrentTriggerData(triggerNode);
    setIsTriggerModalOpen(true);
  };

  // Handle Reauthorize button click
  const handleIntegrateButtonClick = async (app) => {
    setSelectedAppForIntegration(app);
    if (app) {
      setLoading(true);
      await authenticateApp(app.value, userGoogleId, () =>
        setLoading(false)
      );
    }
  };

  // Fetch bot steps and integrated apps when botId changes
  useEffect(() => {
    if (botId) {
      setLoading(true);
      fetchBotSteps(botId, userGoogleId)
        .then((response) => {
          if (response.error) {
            setLoading(false);
            console.log("Step fetch error:", response.error);
            return;
          }
          console.log("step fetch response:", response.trigger, response.name, response.task_description, response.steps, response.selected_app_integration_options);
          setBotSteps(response.steps);
          setTriggers(response.trigger || []); // Set triggers from response
          // Fetch app/event data needed for the modal
          fetchAppsandEventsData(response.selected_app_integration_options || []);
          setLoading(false);
        })
        .catch(() => setLoading(false));

      getAuthenticatedApps(userGoogleId, botId)
        .then((response) => {
          console.log("auth app response:", response);
          if (response.error) {
            setLoading(false);
            console.log("Apps fetch error:", response.error);
            return;
          }
          const integrated = response.apps;
          console.log("Apps fetched:", integrated);

          // Filter triggerOptions to separate integrated and non-integrated apps
          const integratedApps = apps_data.filter((app) =>
            integrated.includes(app.value)
          );
          const notIntegratedApps = apps_data.filter(
            (app) => !integrated.includes(app.value)
          );
          let currentBot = null;
          for (const bot of bots) {
            if (bot.id === botId) {
              currentBot = bot;
              break;
            }
          }
          console.log("Current bot:", currentBot);
          if (!currentBot) {
            return;
          }
          console.log("Integrated apps:", integratedApps);
          setIntegratedApps(
            integratedApps.map((app) => {
              // Find common functions between bot functionalities and app functions
              const selectedFunctions = app.functions.filter((func) =>
                currentBot.functionalities.includes(func)
              );

              // Return the updated app object with `selectedFunctions`
              return {
                ...app,
                selectedFunctions,
              };
            })
          );

          // setIntegratedApps(integratedApps);
          setNotIntegratedApps(notIntegratedApps);
        })
        .catch((err) => console.log("Failed to fetch apps", err));
    }
  }, [botId]);

  const handleEditClick = (stepId, currentPrompt) => {
    setEditingStepId(stepId);
    setEditedPrompt(currentPrompt);
    setTimeout(() => {
      handleTextareaChange(); // Adjust textarea height right after setting the prompt
    }, 0); // Use a timeout of 0 to wait until the next render cycle
  };

  const handleCancelEdit = () => {
    setEditingStepId(null);
    setEditedPrompt("");
  };

  const handleSubmitEdit = (stepId) => {
    updateBotStepPrompt(botId, stepId, userGoogleId, editedPrompt)
      .then((response) => {
        if (response.error) {
          console.error("Error:", response.error);
          return;
        }

        // Update the state with the new prompt
        setBotSteps((prevSteps) =>
          prevSteps.map((step) =>
            step.id === stepId ? { ...step, prompt: editedPrompt } : step
          )
        );
        handleCancelEdit();
      })
      .catch((err) => console.log("Failed to update prompt", err));
  };
  // Function to automatically resize textarea height
  const handleTextareaChange = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to shrink textarea if needed
      textarea.style.height = "auto";
      // Set the height to scrollHeight to expand it to fit content
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 ${botId ? "block" : "hidden"
        } bg-black bg-opacity-50`}
    >
      <div className="relative p-8 bg-[#1E1F20] text-zinc-100 w-full max-w-3xl mx-auto my-8 rounded-lg shadow-lg">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold">Edit Bot Steps</h2>
          <button
            className="text-gray-500 hover:text-gray-800"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="mt-4 max-h-96 overflow-y-auto hide-sidebar">
          {loading ? (
            <div className="text-center">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full"></div>
            </div>
          ) : (
            <>
              {/* Triggers Section */}
              <h3 className="text-lg text-zinc-200 font-semibold mb-2">
                Triggers:
              </h3>
              <div className="flex flex-wrap gap-3 mb-6">
                {triggers.length > 0 ? (
                  triggers.map((trigger) => (
                    <button
                      key={trigger.id}
                      onClick={() => handleViewTrigger(trigger)}
                      className="flex items-center gap-3 p-2 bg-zinc-900 hover:bg-zinc-800 rounded-lg shadow-sm transition-colors duration-200 text-left"
                    >
                      <div className="bg-zinc-100 rounded-md p-1">
                        <img
                          src={
                            appImages[trigger.data.triggerOption] ||
                            "/assets/logo.png"
                          }
                          alt={trigger.data.label}
                          className="h-6 w-6"
                        />
                      </div>
                      <h4 className="text-sm font-semibold">
                        {trigger.data.label || "Unnamed Trigger"}
                      </h4>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-zinc-400">No triggers configured.</p>
                )}
              </div>
              <h3 className="text-lg text-zinc-200 font-semibold mb-4">
                Integrated Apps:
              </h3>
              <ul className="list-disc mb-6">
                {integratedApps.map((app, index) => (
                  <div
                    key={app.id}
                    className="flex items-center justify-between gap-3 p-2 bg-zinc-100 bg-opacity-10 rounded-lg shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex-shrink-0">
                        <div className="bg-zinc-200 rounded-lg ml-2 p-1">
                          <img
                            alt={app.label}
                            src={app.image}
                            className="h-8 w-8"
                          />
                        </div>
                      </span>
                      <div>
                        <h4 className="text-md font-semibold">{app.label}</h4>
                        <p className="text-sm ">
                          {app.selectedFunctions.join(", ")}
                        </p>
                      </div>
                    </div>

                    <button
                      className="btn btn-secondary mx-2 text-sm mr-2 text-zinc-200 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-all duration-300"
                      onClick={() => handleIntegrateButtonClick(app)}
                    >
                      Reauthorize
                    </button>
                  </div>
                ))}
              </ul>

              <h3 className="text-lg font-semibold mb-4">Bot Steps:</h3>
              <ul className="divide-y divide-zinc-700">
                {botSteps.map((step) => (
                  <li key={step.id} className="py-3">
                    {editingStepId === step.id ? (
                      <div>
                        <textarea
                          ref={textareaRef}
                          value={editedPrompt}
                          onChange={(e) => {
                            setEditedPrompt(e.target.value);
                            handleTextareaChange();
                          }}
                          className="w-full px-3 py-2 border bg-zinc-800 border-zinc-700 rounded-md text-sm mb-4 resize-none overflow-hidden"
                          rows={4} // Use a single row initially
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={handleCancelEdit}
                            className="btn btn-secondary text-sm text-zinc-200 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-all duration-300"
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-secondary text-sm text-zinc-200 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-all duration-300"
                            onClick={() => handleSubmitEdit(step.id)}
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <div>
                          <strong>Step {step.step_index + 1}:</strong>{" "}
                          {step.step_title}
                          <p className="text-sm text-gray-600 mt-1">
                            {step.prompt}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            handleEditClick(step.id, step.step_prompt);
                          }}
                          className="btn btn-secondary mx-2 text-sm mr-2 text-zinc-200 p-2 rounded-lg hover:bg-zinc-900"
                        >
                          <FaPen />
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {isTriggerModalOpen && (
                <TriggerNodeModal
                  isOpen={isTriggerModalOpen}
                  onClose={() => setIsTriggerModalOpen(false)}
                  nodeData={currentTriggerData}
                  appEventData={appEventsData}
                  viewOnly={true} // <-- This is the key prop for read-only mode
                  isBot={true}
                />
              )}
            </>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <button
            className="btn btn-secondary mx-2 text-sm mr-2 text-zinc-200 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 transition-all duration-300"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BotStepsEditModal;
